import { isEmpty } from '@festina-life/core'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { createPrimitiveComparator } from '../../forms/select/Select.utils'
import { useUiContext } from '../../theme'

const getComparableValues = (
  value: object,
  compare: (a: unknown, b: unknown) => number,
): unknown[] =>
  Array.isArray(value)
    ? value
    : Object.entries(value)
        .sort(([aKey], [bKey]) => compare(aKey, bKey))
        .map(([, entryValue]) => entryValue)

export const useComparator = () => {
  const intl = useIntl()
  const { currency } = useUiContext()
  const locale = intl.locale

  return useMemo(() => {
    const comparePrimitives = createPrimitiveComparator({ locale, currency })

    const compare = (a: unknown, b: unknown): number => {
      if (isEmpty(a)) {
        return isEmpty(b) ? 0 : 1
      } else if (isEmpty(b)) {
        return -1
      }

      if (
        typeof a === 'object' &&
        typeof b === 'object' &&
        !(a instanceof Date && b instanceof Date)
      ) {
        const aValues = getComparableValues(a, compare)
        const bValues = getComparableValues(b, compare)

        const longest = Math.max(aValues.length, bValues.length)
        for (let i = 0; i < longest; i += 1) {
          const r = compare(aValues[i], bValues[i])
          if (r) {
            return r
          }
        }
        return 0
      }

      return comparePrimitives(a, b)
    }
    return compare
  }, [locale, currency])
}
